import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Outlet } from "react-router";

import { cn } from "@/lib/cn";
import { IconButton } from "@/ui/IconButton";
import { ToastProvider } from "@/ui/Toast";

import { EnvironmentBadge } from "./EnvironmentBadge";
import { SidebarNav } from "./SidebarNav";
import { UserMenu } from "./UserMenu";

function Wordmark() {
  return (
    <div className="flex items-center gap-3 border-b border-sidebar-border px-5 py-4">
      <span
        aria-hidden="true"
        className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground"
      >
        L1
      </span>
      <span>
        <span className="block text-sm font-semibold leading-tight text-sidebar-foreground">
          OneLiv
        </span>
        <span className="block text-xs leading-tight text-muted-foreground">
          Clinical configuration
        </span>
      </span>
    </div>
  );
}

export function AppShell() {
  const [navOpen, setNavOpen] = useState(false);

  return (
    // The toast viewport lives at the SHELL, not inside any route: a submit
    // toast outlives the modal that raised it and the navigation that follows
    // it (#518), so its region has to be mounted somewhere no route change
    // unmounts.
    <ToastProvider>
      <div className="min-h-screen bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:text-sm focus:shadow-md"
      >
        Skip to main content
      </a>

      {navOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-foreground/30 lg:hidden"
        />
      ) : null}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-sidebar flex-col border-r border-sidebar-border bg-sidebar transition-transform",
          "lg:translate-x-0",
          navOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex items-center justify-between lg:block">
          <Wordmark />
          <div className="pr-3 lg:hidden">
            <IconButton
              label="Close navigation"
              icon={<X className="h-4 w-4" />}
              onClick={() => setNavOpen(false)}
              showTooltip={false}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {/* Following a link closes the small-screen slide-over, which would
              otherwise cover the page the user just asked for. */}
          <SidebarNav onNavigate={() => setNavOpen(false)} />
        </div>
      </aside>

      <div className="lg:pl-sidebar">
        <header className="flex h-16 items-center justify-between gap-4 border-b border-border bg-background px-5">
          <div className="flex items-center gap-3">
            <span className="lg:hidden">
              <IconButton
                label="Open navigation"
                icon={<Menu className="h-4 w-4" />}
                onClick={() => setNavOpen(true)}
                showTooltip={false}
              />
            </span>
            <EnvironmentBadge />
          </div>
          <UserMenu />
        </header>

        <main id="main-content" className="px-5 py-6 lg:px-8">
          <Outlet />
        </main>
      </div>
      </div>
    </ToastProvider>
  );
}
